import React from "react";

interface Props {
  actorEmail?: string;
  actorName?: string;
  changeClass?: string;
  changeClassRule?: string;
  changeClassEvidence?: string;
  originalChangeType?: string;
  attributionOutcome?: string;
  attributedActors?: string[];
  attributionEvidence?: string;
  attributionWindowStart?: string;
  attributionWindowEnd?: string;
}

interface Evidence {
  actor: string;
  operation: string;
  at: string;
}

/**
 * Who is responsible for a change, and how firmly that is known.
 *
 * A snapshot diff has no actor: it is a comparison of two readings taken by
 * this system, so the only party involved is this system. Every detected
 * change therefore used to read "System (snapshot diff)" here, which is
 * accurate and tells a reviewer nothing.
 *
 * There are three better answers, and which one applies is not cosmetic:
 *
 *  - The audit log recorded somebody writing to this artifact inside the
 *    window between the two snapshots. That person is named, marked as
 *    inferred, with the operation and time available on hover. Inferred is not
 *    observed and is not presented as though it were.
 *  - More than one person wrote to it in that window. All of them are named,
 *    because picking one would be a guess presented as a finding.
 *  - The row is not about a change at all — the monitoring gained or lost
 *    sight of the field. Saying so is the honest answer and stops a reviewer
 *    hunting for a culprit who does not exist.
 */
export default function ChangeActor({
  actorEmail,
  actorName,
  changeClass,
  changeClassRule,
  changeClassEvidence,
  originalChangeType,
  attributionOutcome,
  attributedActors,
  attributionEvidence,
  attributionWindowStart,
  attributionWindowEnd,
}: Props) {
  // A first-hand actor from the audit log needs no qualification.
  if (actorEmail) return <span>{actorEmail}</span>;

  if (changeClass === "visibility") {
    const why =
      changeClassEvidence ||
      "The collector gained or lost the ability to read this field. Nothing in Power BI changed.";
    return (
      <span
        className="text-content-muted italic"
        title={
          originalChangeType
            ? `Recorded as "${originalChangeType}" before review — ${why}` +
              (changeClassRule ? ` (${changeClassRule})` : "")
            : why
        }
      >
        Not a change
      </span>
    );
  }

  const actors = attributedActors ?? [];
  if (actors.length > 0) {
    let evidence: Evidence[] = [];
    try {
      evidence = attributionEvidence ? JSON.parse(attributionEvidence) : [];
    } catch {
      evidence = [];
    }

    const window =
      attributionWindowStart && attributionWindowEnd
        ? `Between snapshots at ${attributionWindowStart.slice(11, 16)} and ${attributionWindowEnd.slice(
            11,
            16
          )} UTC.`
        : "";
    const operations = evidence
      .map((e) => `${e.operation} at ${e.at.slice(11, 19)}`)
      .join("; ");

    return (
      <span
        className="text-content"
        title={
          `Inferred from the audit log, not observed directly. ${window} ` +
          (operations ? `Audit events: ${operations}.` : "") +
          (actors.length > 1
            ? " More than one person wrote to this artifact in that window, so any of them may be responsible."
            : "")
        }
      >
        {actors.join(", ")}
        <span className="ml-1 text-xs text-content-subtle">
          {actors.length > 1 ? "(one of these)" : "(inferred)"}
        </span>
      </span>
    );
  }

  if (attributionOutcome === "none_found") {
    return (
      <span
        className="text-content-muted"
        title={
          "No audit-log write to this artifact was found between the two snapshots " +
          "that detected this change. The change is real; the actor is not recorded. " +
          "Changes made outside the Power BI service, or by paths the audit log does " +
          "not cover, look like this."
        }
      >
        Unattributed
      </span>
    );
  }

  if (attributionOutcome === "window_unknown") {
    return (
      <span
        className="text-content-muted"
        title={
          "The earlier of the two snapshots has passed its 90-day retention, so " +
          "there is no bounded window in which to look for the actor."
        }
      >
        Unattributed
      </span>
    );
  }

  // Not yet examined. The audit log is published with minutes-to-hours of
  // latency, so a change detected in the last few hours has not been looked up
  // yet rather than having come back empty.
  if (actorName === "System (snapshot diff)") {
    return (
      <span
        className="text-content-muted"
        title="Detected by comparing snapshots. The audit log has not been searched for an actor yet — this runs hourly, once the log has had time to publish."
      >
        Awaiting attribution
      </span>
    );
  }

  return <span>{actorName || "—"}</span>;
}
